import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Droplet } from 'lucide-react';
import type { BloodStockRow, CrossMatchRecordedResult, ScreenUnitInput } from '@his/shared';
import { get, post, put, qs } from '@/lib/api';
import { tr } from '@/lib/i18n-core';
import { describeError } from '@/lib/auth';
import { cn, dateOnly, humanize } from '@/lib/utils';
import { DataTable, Dialog, FilterField, PageHeader, SearchFilterBar, StatTile, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Select, StatusBadge, statusLabel } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { SCREEN_MARKERS, UNANSWERED_SCREEN, completeScreen, unansweredMarkers, type ScreenAnswers, type ScreenMarker } from './blood-screen';

export interface BloodUnit {
  id: string;
  unitNo: string;
  bloodGroup: string;
  component: string;
  status: string;
  collectedAt: string;
  expiryDate: string;
  screeningPassed: boolean;
  donor: { id: string; donorNo: string; name: string } | null;
}

interface CrossMatch {
  id: string;
  result: string;
  performedAt: string;
  transfusedAt: string | null;
  reactionNote: string | null;
  bloodUnit: BloodUnit;
  patient: { id: string; mrn: string; fullName: string; bloodGroup: string | null } | null;
}

export const GROUP_LABEL = (group: string) => group.replace('_pos', '+').replace('_neg', '−').toUpperCase();

/**
 * Blood bank (SRS 6.9).
 *
 * Stock leads, because the question asked of a blood bank is almost always
 * "what do we have?" — and the answer has to separate what is genuinely
 * available from what is only reserved or about to expire.
 */
export function BloodBankPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => params.get('tab') ?? 'stock');

  return (
    <>
      <PageHeader
        title={tr('lab', 'bb_title')}
        description={tr('lab', 'bb_description')}
        actions={
          tab === 'crossmatch' ? (
            <Button onClick={() => navigate('/blood-bank/requests/new')}>{tr('lab', 'bb_request_cross_match')}</Button>
          ) : tab === 'donors' ? (
            <Button onClick={() => navigate('/blood-bank/donors/new')}>{tr('lab', 'bb_register_donor')}</Button>
          ) : null
        }
      />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'stock', label: tr('lab', 'bb_tab_stock') },
          { id: 'crossmatch', label: tr('lab', 'bb_tab_cross_match') },
          { id: 'donors', label: tr('lab', 'bb_tab_donors') },
        ]}
      />
      {tab === 'stock' && <StockTab />}
      {tab === 'crossmatch' && <CrossMatchTab />}
      {tab === 'donors' && <DonorsTab />}
    </>
  );
}

function StockTab() {
  const [status, setStatus] = useState('available');

  const stock = useQuery({ queryKey: ['blood-stock'], queryFn: () => get<BloodStockRow[]>('/blood-bank/stock') });
  const units = useQuery({
    queryKey: ['blood-units', status],
    queryFn: () => get<BloodUnit[]>(`/blood-bank/units${qs({ status: status || undefined })}`),
  });

  const rows = stock.data ?? [];
  const expiring = rows.reduce((sum, row) => sum + row.expiringWithin7Days, 0);

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatTile label={tr('lab', 'bb_units_available')} value={rows.reduce((sum, r) => sum + r.available, 0)} tone="success" />
        <StatTile label={tr('lab', 'bb_reserved')} value={rows.reduce((sum, r) => sum + r.reserved, 0)} />
        <StatTile label={tr('lab', 'bb_expiring_within_a_week')} value={expiring} tone={expiring > 0 ? 'warning' : 'default'} />
      </div>

      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((row) => (
          <div
            key={`${row.bloodGroup}-${row.component}`}
            className={cn(
              'rounded-lg border-2 p-3',
              row.available === 0
                ? 'border-destructive/40 bg-destructive/5'
                : row.available <= 2
                  ? 'border-warning/40 bg-warning/5'
                  : 'border-success/40 bg-success/5',
            )}
          >
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-lg font-semibold">
                <Droplet className="h-4 w-4" /> {GROUP_LABEL(row.bloodGroup)}
              </span>
              <Badge variant="muted">{componentLabel(row.component)}</Badge>
            </div>
            <p className="mt-1 text-2xl font-semibold tabular">{row.available}</p>
            <p className="text-xs text-muted-foreground">
              {tr('lab', 'bb_x_reserved', { count: row.reserved })}
              {row.expiringWithin7Days > 0 && ` · ${tr('lab', 'bb_x_expiring', { count: row.expiringWithin7Days })}`}
            </p>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-full rounded-lg border p-8 text-center text-sm text-muted-foreground">
            {tr('lab', 'bb_no_units_in_stock')}
          </div>
        )}
      </div>

      <SearchFilterBar
        filters={
          <FilterField label={tr('lab', 'status')}>
            <Select className="w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{tr('lab', 'bb_all_units')}</option>
              {['quarantine', 'available', 'reserved', 'issued', 'transfused', 'discarded', 'expired'].map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s, 'blood_unit_status')}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={status !== 'available' ? () => setStatus('available') : undefined}
      />

      <DataTable
        rows={units.data}
        loading={units.isLoading} refreshing={units.isFetching}
        error={units.error}
        rowKey={(row) => row.id}
        empty={tr('lab', 'bb_no_units_match')}
        columns={[
          { header: tr('lab', 'unit'), sort: (row) => row.unitNo, cell: (row) => <span className="font-mono text-xs">{row.unitNo}</span> },
          { header: tr('lab', 'bb_group'), sort: (row) => row.bloodGroup, cell: (row) => <Badge variant="destructive">{GROUP_LABEL(row.bloodGroup)}</Badge> },
          { header: tr('lab', 'bb_component'), sort: (row) => row.component, cell: (row) => componentLabel(row.component) },
          { header: tr('lab', 'bb_donor'), sort: (row) => row.donor?.name, cell: (row) => row.donor?.name ?? '—' },
          { header: tr('lab', 'collected'), sort: (row) => row.collectedAt, cell: (row) => dateOnly(row.collectedAt) },
          {
            header: tr('lab', 'expires'),
            sort: (row) => row.expiryDate,
            cell: (row) => (
              <span className={cn(new Date(row.expiryDate) < new Date() ? 'font-semibold text-destructive' : '')}>
                {dateOnly(row.expiryDate)}
              </span>
            ),
          },
          {
            header: tr('lab', 'bb_screened'),
            sort: (row) => row.screeningPassed,
            cell: (row) =>
              row.screeningPassed ? (
                <Badge variant="success">{tr('lab', 'bb_screen_clear')}</Badge>
              ) : (
                <Badge variant="warning">{tr('lab', 'bb_screen_pending')}</Badge>
              ),
          },
          { header: tr('lab', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} group="blood_unit_status" /> },
        ]}
      />
    </>
  );
}

function CrossMatchTab() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const crossMatches = useQuery({
    queryKey: ['cross-matches'],
    queryFn: () => get<CrossMatch[]>('/blood-bank/cross-matches?limit=200'),
  });

  const act = useMutation({
    mutationFn: ({ id, action, result }: { id: string; action: string; result?: CrossMatchRecordedResult }) =>
      action === 'result'
        ? put(`/blood-bank/cross-matches/${id}/result`, { result })
        : post(`/blood-bank/cross-matches/${id}/${action}`, action === 'transfusion' ? {} : undefined),
    onSuccess: () => {
      toast.success(tr('lab', 'bb_updated'));
      void queryClient.invalidateQueries({ queryKey: ['cross-matches'] });
      void queryClient.invalidateQueries({ queryKey: ['blood-stock'] });
      void queryClient.invalidateQueries({ queryKey: ['blood-units'] });
    },
    onError: (error) => toast.error(tr('lab', 'bb_could_not_update_the_cross_match'), describeError(error)),
  });

  const running = (id: string, action: string, result?: CrossMatchRecordedResult) =>
    act.isPending && act.variables?.id === id && act.variables.action === action && act.variables.result === result;
  const busy = (id: string) => act.isPending && act.variables?.id === id;

  return (
    <>
      <DataTable
        rows={crossMatches.data}
        loading={crossMatches.isLoading} refreshing={crossMatches.isFetching}
        error={crossMatches.error}
        rowKey={(row) => row.id}
        empty={tr('lab', 'bb_no_cross_matches_requested')}
        columns={[
          {
            header: tr('lab', 'patient'),
            sort: (row) => row.patient?.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient?.fullName ?? '—'}</p>
                <p className="text-xs text-muted-foreground">
                  {row.patient?.mrn}
                  {row.patient?.bloodGroup ? ` · ${GROUP_LABEL(row.patient.bloodGroup)}` : ''}
                </p>
              </div>
            ),
          },
          {
            header: tr('lab', 'unit'),
            sort: (row) => row.bloodUnit.unitNo,
            cell: (row) => (
              <div>
                <p className="font-mono text-xs">{row.bloodUnit.unitNo}</p>
                <p className="text-xs text-muted-foreground">
                  {GROUP_LABEL(row.bloodUnit.bloodGroup)} · {componentLabel(row.bloodUnit.component)}
                </p>
              </div>
            ),
          },
          { header: tr('lab', 'bb_requested'), sort: (row) => row.performedAt, cell: (row) => dateOnly(row.performedAt) },
          { header: tr('lab', 'result'), sort: (row) => row.result, cell: (row) => <StatusBadge status={row.result} /> },
          {
            header: tr('lab', 'bb_reaction'),
            sort: (row) => Boolean(row.reactionNote),
            cell: (row) =>
              row.reactionNote ? (
                <span className="inline-flex items-center gap-1 text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5" /> {tr('lab', 'bb_reaction_recorded')}
                </span>
              ) : (
                '—'
              ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                {row.result === 'pending' && (
                  <>
                    <Button
                      size="sm"
                      loading={running(row.id, 'result', 'compatible')}
                      disabled={busy(row.id)}
                      onClick={() => act.mutate({ id: row.id, action: 'result', result: 'compatible' })}
                    >
                      {tr('lab', 'compatible')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      loading={running(row.id, 'result', 'incompatible')}
                      disabled={busy(row.id)}
                      onClick={() => act.mutate({ id: row.id, action: 'result', result: 'incompatible' })}
                    >
                      {tr('lab', 'incompatible')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={running(row.id, 'result', 'cancelled')}
                      disabled={busy(row.id)}
                      onClick={() => act.mutate({ id: row.id, action: 'result', result: 'cancelled' })}
                    >
                      {tr('lab', 'cancel_request_and_release_unit')}
                    </Button>
                  </>
                )}
                {row.result === 'compatible' && (
                  <Button
                    size="sm"
                    loading={running(row.id, 'issue')}
                    disabled={busy(row.id)}
                    onClick={() => act.mutate({ id: row.id, action: 'issue' })}
                  >
                    {tr('lab', 'issue_unit')}
                  </Button>
                )}
                {row.result === 'issued' && (
                  <Button
                    size="sm"
                    loading={running(row.id, 'transfusion')}
                    disabled={busy(row.id)}
                    onClick={() => act.mutate({ id: row.id, action: 'transfusion' })}
                  >
                    {tr('lab', 'record_transfusion')}
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

    </>
  );
}


function DonorsTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [collectFor, setCollectFor] = useState<{ id: string; name: string } | null>(null);
  const [screening, setScreening] = useState<{ id: string; unitNo: string } | null>(null);

  const donors = useQuery({
    queryKey: ['donors', search],
    queryFn: () =>
      get<
        {
          id: string;
          donorNo: string;
          name: string;
          bloodGroup: string;
          phone: string | null;
          lastDonatedAt: string | null;
          isEligible: boolean;
        }[]
      >(`/blood-bank/donors${qs({ q: search || undefined, limit: 200 })}`),
  });

  const collect = useMutation({
    mutationFn: (component: string) => post<{ id: string; unitNo: string }>('/blood-bank/units', { donorId: collectFor?.id, component }),
    onSuccess: (unit) => {
      toast.success(tr('lab', 'bb_unit_collected'), tr('lab', 'bb_unit_collected_hint'));
      void queryClient.invalidateQueries({ queryKey: ['donors'] });
      void queryClient.invalidateQueries({ queryKey: ['blood-units'] });
      setCollectFor(null);
      setScreening(unit);
    },
    onError: (error) => toast.error(tr('lab', 'bb_could_not_collect'), describeError(error)),
  });

  return (
    <>
      <SearchFilterBar
        search={{ value: search, onChange: setSearch, placeholder: tr('lab', 'bb_donor_search_placeholder') }}
        onReset={search ? () => setSearch('') : undefined}
      />

      <DataTable
        rows={donors.data}
        loading={donors.isLoading} refreshing={donors.isFetching}
        error={donors.error}
        rowKey={(row) => row.id}
        empty={tr('lab', 'bb_no_donors_registered')}
        columns={[
          { header: tr('lab', 'bb_donor_no'), sort: (row) => row.donorNo, cell: (row) => <span className="font-mono text-xs">{row.donorNo}</span> },
          { header: tr('lab', 'name'), sort: (row) => row.name, cell: (row) => row.name },
          { header: tr('lab', 'bb_group'), sort: (row) => row.bloodGroup, cell: (row) => <Badge variant="destructive">{GROUP_LABEL(row.bloodGroup)}</Badge> },
          { header: tr('lab', 'bb_phone'), sort: (row) => row.phone, cell: (row) => row.phone ?? '—' },
          {
            header: tr('lab', 'bb_last_donated'),
            sort: (row) => row.lastDonatedAt,
            cell: (row) => (row.lastDonatedAt ? dateOnly(row.lastDonatedAt) : tr('lab', 'never')),
          },
          {
            header: tr('lab', 'bb_eligible'),
            sort: (row) => row.isEligible,
            cell: (row) =>
              row.isEligible ? (
                <Badge variant="success">{tr('lab', 'bb_eligible_yes')}</Badge>
              ) : (
                <Badge variant="destructive">{tr('lab', 'bb_eligible_retired')}</Badge>
              ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.isEligible && (
                <Button size="sm" onClick={() => setCollectFor({ id: row.id, name: row.name })}>
                  {tr('lab', 'bb_collect')}
                </Button>
              ),
          },
        ]}
      />

      <Dialog
        open={Boolean(collectFor)}
        onClose={() => setCollectFor(null)}
        title={tr('lab', 'bb_collect_from_x', { name: collectFor?.name ?? '' })}
        description={tr('lab', 'bb_shelf_life_hint')}
      >
        <div className="grid gap-2 sm:grid-cols-2">
          {['whole_blood', 'prbc', 'ffp', 'platelets', 'cryoprecipitate'].map((component) => (
            <Button key={component} variant="outline" onClick={() => collect.mutate(component)} loading={collect.isPending}>
              {componentLabel(component)}
            </Button>
          ))}
        </div>
      </Dialog>

      {screening !== null && (
        <ScreeningDialog unit={screening} onClose={() => setScreening(null)} />
      )}
    </>
  );
}


function markerLabel(marker: ScreenMarker): string {
  switch (marker) {
    case 'hiv':
      return tr('lab', 'bb_marker_hiv');
    case 'hepatitisB':
      return tr('lab', 'bb_marker_hepatitis_b');
    case 'hepatitisC':
      return tr('lab', 'bb_marker_hepatitis_c');
    case 'syphilis':
      return tr('lab', 'bb_marker_syphilis');
    case 'malaria':
      return tr('lab', 'bb_marker_malaria');
  }
}

export function componentLabel(component: string): string {
  switch (component) {
    case 'whole_blood':
      return tr('lab', 'bb_component_whole_blood');
    case 'prbc':
      return tr('lab', 'bb_component_prbc');
    case 'ffp':
      return tr('lab', 'bb_component_ffp');
    case 'platelets':
      return tr('lab', 'bb_component_platelets');
    case 'cryoprecipitate':
      return tr('lab', 'bb_component_cryoprecipitate');
    default:
      return humanize(component);
  }
}

function ScreeningDialog({ unit, onClose }: { unit: { id: string; unitNo: string } | null; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [markers, setMarkers] = useState<ScreenAnswers>(UNANSWERED_SCREEN);
  const [tried, setTried] = useState(false);
  const unanswered = unansweredMarkers(markers).length;

  const screen = useMutation({
    mutationFn: (answers: ScreenUnitInput) => put<{ status: string }>(`/blood-bank/units/${unit?.id}/screening`, answers),
    onSuccess: (result) => {
      toast[result.status === 'available' ? 'success' : 'error'](
        result.status === 'available' ? tr('lab', 'bb_screen_released') : tr('lab', 'bb_screen_discarded'),
        result.status === 'available' ? undefined : tr('lab', 'bb_donor_retired'),
      );
      void queryClient.invalidateQueries({ queryKey: ['blood-units'] });
      void queryClient.invalidateQueries({ queryKey: ['blood-stock'] });
      void queryClient.invalidateQueries({ queryKey: ['donors'] });
      onClose();
    },
    onError: (error) => toast.error(tr('lab', 'bb_could_not_record_the_screen'), describeError(error)),
  });

  const record = () => {
    setTried(true);
    const answers = completeScreen(markers);
    if (answers) screen.mutate(answers);
  };

  return (
    <Dialog
      open={Boolean(unit)}
      onClose={onClose}
      title={tr('lab', 'bb_infection_screen_x', { unit: unit?.unitNo ?? '' })}
      description={tr('lab', 'bb_screen_description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'later')}
          </Button>
          <Button onClick={record} loading={screen.isPending}>
            {tr('lab', 'bb_record_screen')}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        {SCREEN_MARKERS.map((marker) => {
          const answer = markers[marker];
          const missing = tried && answer === null;
          return (
            <div
              key={marker}
              role="radiogroup"
              aria-label={markerLabel(marker)}
              aria-invalid={missing || undefined}
              className={cn(
                'flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2',
                answer === true ? 'border-destructive/50 bg-destructive/5' : missing ? 'border-warning/60 bg-warning/5' : 'border-border',
              )}
            >
              <span className={cn('text-sm font-medium', answer === true && 'text-destructive')}>{markerLabel(marker)}</span>
              <div className="flex gap-1">
                {([false, true] as const).map((value) => (
                  <label
                    key={String(value)}
                    className={cn(
                      'inline-flex cursor-pointer items-center rounded-md border px-2.5 py-1 text-xs font-medium transition-colors focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-ring',
                      answer === value
                        ? value
                          ? 'border-destructive bg-destructive/10 text-destructive'
                          : 'border-success bg-success/10 text-success'
                        : 'border-border text-muted-foreground hover:bg-accent',
                    )}
                  >
                    <input
                      type="radio"
                      className="sr-only"
                      name={`screen-${marker}`}
                      checked={answer === value}
                      onChange={() => setMarkers((current) => ({ ...current, [marker]: value }))}
                    />
                    {value ? tr('lab', 'bb_reactive') : tr('lab', 'bb_negative')}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
        {tried && unanswered > 0 && <Alert variant="destructive">{tr('lab', 'bb_answer_every_marker', { count: unanswered })}</Alert>}
        <Alert variant="warning">{tr('lab', 'bb_reactive_warning')}</Alert>
      </div>
    </Dialog>
  );
}
